import { type AssetCollection, AssetPanel } from "@hoylu/client-common";
import { decode } from "html-entities";
import React, { useCallback, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { templateRequests } from "../services/templates/template.requests";
import {
  ORGANIZATION_CATEGORY_ID,
  QUICK_ACCESS_CATEGORY_ID,
} from "../services/templates/types";
import { workspaceRequests } from "../services/workspaces/workspaces.requests";
import { config, documentMetadata } from "../state/config/config.selector";
import { fetchTemplateCategories } from "../state/templates/templates.actions";
import { templateCategories as selectTemplateCategories } from "../state/templates/templates.selectors";
import type {
  TemplateCategoryState,
  TemplateMetadata,
} from "../state/templates/types";
import { user } from "../state/user/user.selector";
import {
  cancelDashboardOption,
  createWorkspaceFromTemplate,
} from "../state/workspaces/workspaces.actions";
import { getWorkspaceThumbnailUrl } from "../state/workspaces/workspaces.selector";
import { useI18n } from "../utils/hooks/use.i18n";
import {
  type TemplateAsset,
  WorkspaceTemplateCard,
} from "./cards/WorkspaceTemplateCard";
import { Modal } from "./modals/Modal";

const FALLBACK_CATEGORY_ICON_MAP = new Map<string, string>([
  ["xStrategy & Planning", "hoylu-ui-icons-distribute"],
  ["xBrainstorming & Ideation", "hoylu-ui-icons-bulb"],
  ["xAgile & Lean", "hoylu-ui-icons-agile-wheel"],
  ["xConstruction", "hoylu-ui-icons-construction"],
]);

const initialTemplateCache = new Map<string, TemplateAsset[]>();

export const DashboardAssetPanel = () => {
  const dispatch = useDispatch();
  const t = useI18n();

  const documentMetadataOrigin = useSelector(documentMetadata);
  const currentUser = useSelector(user);
  const templateCategories = useSelector(selectTemplateCategories);
  const conf = useSelector(config);

  const getTemplateThumbnailUrl = (workspaceId: string) =>
    getWorkspaceThumbnailUrl(workspaceId, conf);

  const [templateCache, setTemplateCache] =
    React.useState(initialTemplateCache);

  useEffect(() => {
    dispatch(fetchTemplateCategories.request());
  }, [dispatch]);

  const getTemplates = useCallback(
    (categoryId: string) =>
      templateRequests.getTemplates(
        documentMetadataOrigin,
        currentUser.token,
        categoryId,
      ),
    [documentMetadataOrigin, currentUser.token],
  );

  const getOrganizationTemplates = useCallback(
    () =>
      templateRequests.getOrganizationTemplates(
        documentMetadataOrigin,
        currentUser.token,
      ),
    [documentMetadataOrigin, currentUser.token],
  );

  const getTemplateDetails = useCallback(
    (workspaceId: string) =>
      workspaceRequests.getTemplateDocumentMetadata(
        documentMetadataOrigin,
        currentUser.token,
        workspaceId,
      ),
    [documentMetadataOrigin, currentUser.token],
  );

  const createFromTemplate = useCallback(
    async (workspaceId: string) => {
      const documentMetadata =
        await getTemplateDetails(workspaceId).toPromise();
      dispatch(createWorkspaceFromTemplate(documentMetadata));
    },
    [dispatch],
  );

  const closeAssetPanel = () => dispatch(cancelDashboardOption());

  const toWorkspaceAssets = useCallback(
    (templates: TemplateMetadata[]): TemplateAsset[] => {
      return templates.map((template) => ({
        assetId: template.workspaceId,
        templateName: decode(template.name), // TODO: is uri encoding really the best way to do this?
        templateDescription: decode(template.description),
        fetchThumbnail: () => {
          const url = getTemplateThumbnailUrl(template.workspaceId);

          return fetch(url + "?defyCache=" + Date.now(), {
            headers: { Authorization: `Bearer ${currentUser.token}` },
          });
        },
        onCreate: () => createFromTemplate(template.workspaceId),
      }));
    },
    [createFromTemplate],
  );

  const getAssetsForTemplateCategory = useCallback(
    async (category: TemplateCategoryState): Promise<TemplateAsset[]> => {
      // return cached values if they exist
      if (templateCache.has(category.categoryId))
        return templateCache.get(category.categoryId)!;

      // fetching templates from service here instead of getting it from state, to fit AssetPanels interface
      const response =
        category.categoryId === ORGANIZATION_CATEGORY_ID
          ? await getOrganizationTemplates().toPromise()
          : await getTemplates(category.categoryId).toPromise();

      const assets = toWorkspaceAssets(response.templates);
      // add to cache
      setTemplateCache((cache) => {
        cache.set(category.categoryId, assets);
        return cache;
      });
      return assets;
    },
    [templateCache, toWorkspaceAssets],
  );

  const workspaceTemplatesCollection: AssetCollection<TemplateAsset>[] =
    useMemo(() => {
      return templateCategories
        .filter((cat) => cat.categoryId !== QUICK_ACCESS_CATEGORY_ID)
        .map((cat) => ({
          key: cat.name,
          icon:
            cat.icon ??
            FALLBACK_CATEGORY_ICON_MAP.get(decode(cat.name)) ??
            "hoylu-ui-icons-palette-brush",
          name: decode(cat.name),
          AssetsRenderer: WorkspaceTemplateCard,
          getAssets: () => getAssetsForTemplateCategory(cat),
          filters: [],
        }));
    }, [toWorkspaceAssets, templateCategories]);

  return (
    <Modal handleOutsideClick={closeAssetPanel}>
      <AssetPanel
        assetCollection={workspaceTemplatesCollection}
        headerIcons={[
          {
            icon: "hoylu-ui-icons-incomplete",
            onClick: closeAssetPanel,
            title: t("CLOSE_BUTTON.CLOSE"),
          },
        ]}
        isDashboardPanel
        locales={{
          USE_COMPACT_VIEW: t("ASSET_PANEL.USE_COMPACT_VIEW"),
          USE_EXPANDED_VIEW: t("ASSET_PANEL.USE_EXPANDED_VIEW"),
        }}
        title={t("WORKSPACE_TEMPLATES.TEMPLATES")}
      />
    </Modal>
  );
};
